import { Injectable, computed, signal } from '@angular/core';

export type SyncState = 'idle' | 'syncing' | 'success' | 'error';

/** Assessment whose local data is at risk when sync stays failed. */
export interface SyncFailedAssessment {
  clientId?: number;
  assessmentId: number;
  exportable: boolean;
}

/** Whether the failure banner can offer a ZIP export of failed assessments. */
export interface SyncExportStatus {
  canExport: boolean;
  reason?: string;
}

export interface SyncNotificationPayload {
  state: SyncState;
  progress?: number;
  totalItems?: number;
  syncedItems?: number;
  errorMessage?: string;
  failedAssessments?: SyncFailedAssessment[];
}

const SYNC_BROADCAST_CHANNEL = 'symphony_data_sync_channel';

@Injectable({
  providedIn: 'root',
})
export class SyncNotificationService {
  private readonly channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(SYNC_BROADCAST_CHANNEL) : null;

  readonly state = signal<SyncState>('idle');
  readonly progress = signal<number | undefined>(undefined);
  readonly totalItems = signal<number | undefined>(undefined);
  readonly syncedItems = signal<number | undefined>(undefined);
  readonly errorMessage = signal<string | undefined>(undefined);
  readonly failedAssessments = signal<SyncFailedAssessment[]>([]);
  readonly exportableAssessments = computed(() =>
    this.failedAssessments().filter((item) => item.exportable && item.clientId != null)
  );
  readonly exportStatus = computed<SyncExportStatus>(() => {
    if (!this.failedAssessments().length) {
      return { canExport: false, reason: 'No failed assessment to export' };
    }
    if (!this.exportableAssessments().length) {
      return { canExport: false, reason: 'No local data available to export' };
    }
    return { canExport: true };
  });

  constructor() {
    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent<SyncNotificationPayload>) => {
        if (event.data) {
          this.applyState(event.data);
        }
      };
    }
  }

  startSync(totalItems?: number): void {
    this.broadcast({
      state: 'syncing',
      progress: undefined,
      totalItems,
      syncedItems: 0,
      errorMessage: undefined,
    });
  }

  updateProgress(synced: number, total: number): void {
    const percent = total > 0 ? Math.min(100, Math.round((synced / total) * 100)) : 100;
    this.broadcast({
      state: 'syncing',
      progress: percent,
      totalItems: total,
      syncedItems: synced,
    });
  }

  completeSync(): void {
    this.broadcast({
      state: 'success',
      progress: 100,
    });
  }

  failSync(errorMessage: string, failedAssessments: SyncFailedAssessment[] = []): void {
    this.broadcast({
      state: 'error',
      errorMessage,
      failedAssessments,
    });
  }

  reset(): void {
    this.broadcast({
      state: 'idle',
      progress: undefined,
      errorMessage: undefined,
      failedAssessments: [],
    });
  }

  private broadcast(payload: SyncNotificationPayload): void {
    this.applyState(payload);
    try {
      this.channel?.postMessage(payload);
    } catch {
      // Ignore broadcast errors
    }
  }

  private applyState(payload: SyncNotificationPayload): void {
    if (payload.state !== undefined) this.state.set(payload.state);
    if (payload.progress !== undefined) this.progress.set(payload.progress);
    if (payload.totalItems !== undefined) this.totalItems.set(payload.totalItems);
    if (payload.syncedItems !== undefined) this.syncedItems.set(payload.syncedItems);
    if (payload.errorMessage !== undefined) this.errorMessage.set(payload.errorMessage);
    if (payload.failedAssessments !== undefined) this.failedAssessments.set(payload.failedAssessments);
  }
}
